/**
 * Settings module shells (M21.15, criterion 25).
 *
 *   /settings/users         → Users & Roles (role reference + invite)
 *   /settings/notifications → per-user channel preferences
 *   /settings/audit-log     → read-only audit stream over the
 *                             *_event tables
 *
 * Users & Roles and Notifications are shells — the IA + structure
 * are in place; the Clerk-Org member sync and server-persisted
 * preferences are follow-ups. Audit Log is fully functional against
 * GET /api/audit-log.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../../components/Loading";
import { PageHeader } from "../../components/ui";
import { api } from "../../lib/api";

// UsersRolesPage now lives in its own file (Step 3 of in-app user
// management) — re-exported here so existing App.tsx imports keep
// working without churn.
export { UsersRolesPage } from "./UsersRolesPage";

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

const _CATEGORY_KEYS: { id: string; labelKey: string }[] = [
  { id: "Approvals", labelKey: "app.settings_cat_approvals" },
  { id: "Compliance", labelKey: "app.settings_cat_compliance" },
  { id: "AI Escalations", labelKey: "app.settings_cat_ai_escalations" },
  { id: "System", labelKey: "app.settings_cat_system" },
];
const _CHANNELS = ["in_app", "email", "whatsapp"] as const;
const _CHANNEL_LABEL_KEYS: Record<(typeof _CHANNELS)[number], string> = {
  in_app: "app.settings_ch_in_app",
  email: "app.settings_ch_email",
  whatsapp: "app.settings_ch_whatsapp",
};
const _PREFS_KEY = "solvait-aihr.notification-prefs";

type Prefs = Record<string, boolean>;

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(_PREFS_KEY);
    return raw ? (JSON.parse(raw) as Prefs) : {};
  } catch {
    return {};
  }
}

export function NotificationsPage() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);

  function toggle(category: string, channel: string) {
    const key = `${category}:${channel}`;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      localStorage.setItem(_PREFS_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable — non-fatal.
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("app.settings_notifications_title")}
        subtitle={t("app.settings_notifications_subtitle")}
        breadcrumbs={[{ label: t("app.settings_breadcrumb"), href: "/settings" }]}
      />
      <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface-default">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="px-4 py-2 text-start">{t("app.settings_col_category")}</th>
              {_CHANNELS.map((c) => (
                <th key={c} className="px-4 py-2 text-center">
                  {t(_CHANNEL_LABEL_KEYS[c])}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {_CATEGORY_KEYS.map((cat) => (
              <tr key={cat.id}>
                <td className="px-4 py-2.5 font-medium text-text-primary">
                  {t(cat.labelKey)}
                </td>
                {_CHANNELS.map((ch) => (
                  <td key={ch} className="px-4 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={prefs[`${cat.id}:${ch}`] ?? false}
                      onChange={() => toggle(cat.id, ch)}
                      aria-label={`${t(cat.labelKey)} — ${t(_CHANNEL_LABEL_KEYS[ch])}`}
                      className="h-4 w-4 rounded border-border-subtle"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-text-tertiary">
        {t("app.settings_notif_local_note")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit Log
// ---------------------------------------------------------------------------

const _SOURCES = ["all", "workflow", "employee", "contract", "chat"];
const _PAGE_SIZE = 50;

export function AuditLogPage() {
  const { t } = useTranslation();
  // Read ?actor= and ?source= so callers (notably the
  // /ai/activity-log redirect from M26 PR 4) can deep-link into a
  // pre-filtered audit view. `actor=ai` is the virtual "AI actors"
  // super-filter — see _AI_ACTORS in api/routes/audit_log.py.
  const [searchParams] = useSearchParams();
  const urlActor = (searchParams.get("actor") ?? "").trim();
  const urlSource = (searchParams.get("source") ?? "").trim();

  const [source, setSource] = useState(
    _SOURCES.includes(urlSource) ? urlSource : "all",
  );
  const [actor, setActor] = useState(urlActor);
  const [actorQuery, setActorQuery] = useState(urlActor);
  const [offset, setOffset] = useState(0);
  const [undoError, setUndoError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // If the URL changes after mount (e.g. user navigates via in-app
  // links), reflect the new filters in state. Effect compares
  // primitives so it doesn't churn on every render.
  useEffect(() => {
    if (urlActor !== actorQuery) {
      setActor(urlActor);
      setActorQuery(urlActor);
      setOffset(0);
    }
    if (urlSource && urlSource !== source && _SOURCES.includes(urlSource)) {
      setSource(urlSource);
      setOffset(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlActor, urlSource]);

  const q = useQuery({
    queryKey: ["audit-log", source, actorQuery, offset],
    queryFn: () =>
      api.getAuditLog({
        source,
        actor: actorQuery || undefined,
        limit: _PAGE_SIZE,
        offset,
      }),
  });

  // M24.5 — undo a reversible chat mutation within the 24h window.
  const undo = useMutation({
    mutationFn: (auditEventId: string) =>
      api.undoChatMutation(auditEventId),
    onSuccess: () => {
      setUndoError(null);
      void queryClient.invalidateQueries({ queryKey: ["audit-log"] });
    },
    onError: (e) => setUndoError((e as Error).message),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={
          actorQuery === "ai"
            ? t("app.settings_audit_ai_title")
            : t("app.settings_audit_title")
        }
        subtitle={
          actorQuery === "ai"
            ? t("app.settings_audit_ai_subtitle")
            : t("app.settings_audit_subtitle")
        }
        breadcrumbs={
          actorQuery === "ai"
            ? [
                { label: t("nav.ai"), href: "/ai" },
                { label: t("app.settings_activity_log_breadcrumb") },
              ]
            : [{ label: t("app.settings_breadcrumb"), href: "/settings" }]
        }
      />

      {actorQuery === "ai" && (
        <div className="flex items-center gap-2 rounded-lg border border-ai-subtle bg-ai-bg px-3 py-2 text-xs text-text-secondary">
          <Sparkles size={14} className="text-ai" aria-hidden />
          <span>
            {t("app.settings_ai_actor_note")}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs font-medium text-text-secondary">
            {t("app.settings_col_source")}
          </span>
          <select
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setOffset(0);
            }}
            className="mt-1 rounded-lg border border-border-subtle px-3 py-1.5 text-sm"
          >
            {_SOURCES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs font-medium text-text-secondary">
            {t("app.settings_col_actor")}
          </span>
          <input
            type="text"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setActorQuery(actor.trim());
                setOffset(0);
              }
            }}
            placeholder={t("app.settings_actor_placeholder")}
            className="mt-1 rounded-lg border border-border-subtle px-3 py-1.5 text-sm"
          />
        </label>
      </div>

      {undoError && <ErrorPanel message={undoError} />}

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorPanel message={(q.error as Error).message} />
      ) : q.data ? (
        <>
          <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface-default">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="px-4 py-2 text-start">{t("app.settings_col_when")}</th>
                  <th className="px-4 py-2 text-start">{t("app.settings_col_source")}</th>
                  <th className="px-4 py-2 text-start">{t("app.settings_col_event")}</th>
                  <th className="px-4 py-2 text-start">{t("app.settings_col_actor")}</th>
                  <th className="px-4 py-2 text-start">{t("app.settings_col_summary")}</th>
                  <th className="px-4 py-2 text-start">{t("app.settings_col_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {q.data.entries.map((e, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 font-mono text-xs text-text-tertiary">
                      {e.occurred_at.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-2">
                      <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] uppercase text-text-secondary">
                        {e.source}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-text-primary">
                      {e.event_type}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-text-secondary">
                      {e.actor ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-text-secondary">
                      <div className="flex items-start gap-1.5">
                        {e.feedback_sentiment === "up" && (
                          <span
                            title={
                              e.feedback_comment ??
                              t("app.chat_action_thumbs_up")
                            }
                            className="inline-flex shrink-0 items-center rounded bg-emerald-50 px-1 py-0.5 text-[11px] font-medium text-emerald-700"
                          >
                            👍
                          </span>
                        )}
                        {e.feedback_sentiment === "down" && (
                          <span
                            title={
                              e.feedback_comment ??
                              t("app.chat_action_thumbs_down")
                            }
                            className="inline-flex shrink-0 items-center rounded bg-rose-50 px-1 py-0.5 text-[11px] font-medium text-rose-700"
                          >
                            👎
                          </span>
                        )}
                        <span>{e.summary}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {e.reversed ? (
                        <span className="text-[11px] text-text-tertiary">
                          {t("app.settings_undone")}
                        </span>
                      ) : e.reversible && e.entry_id ? (
                        <button
                          type="button"
                          onClick={() =>
                            undo.mutate(e.entry_id as string)
                          }
                          disabled={undo.isPending}
                          className="inline-flex items-center gap-1 rounded border border-border-subtle px-1.5 py-0.5 text-[11px] text-text-secondary hover:bg-surface-muted disabled:opacity-50"
                        >
                          <RotateCcw size={11} /> {t("app.settings_undo")}
                        </button>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {q.data.entries.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-sm text-text-tertiary"
                    >
                      {t("app.settings_no_events")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-text-secondary">
            <span>
              {t(
                q.data.total === 1
                  ? "app.settings_pagination_one"
                  : "app.settings_pagination_many",
                {
                  total: q.data.total,
                  from: offset + 1,
                  to: Math.min(offset + _PAGE_SIZE, q.data.total),
                },
              )}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() =>
                  setOffset(Math.max(0, offset - _PAGE_SIZE))
                }
                className="rounded-lg border border-border-subtle px-3 py-1.5 disabled:opacity-40"
              >
                {t("app.settings_previous")}
              </button>
              <button
                type="button"
                disabled={offset + _PAGE_SIZE >= q.data.total}
                onClick={() => setOffset(offset + _PAGE_SIZE)}
                className="rounded-lg border border-border-subtle px-3 py-1.5 disabled:opacity-40"
              >
                {t("app.settings_next")}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
